/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

// used by Pagination in tables
export const TABLE_PAGE_SIZES = [5, 10];
// Defaults for UI to select models/voices
export const DEFAULT_BROADBAND_MODEL = 'en-US_BroadbandModel';
export const NARROWBAND = 'narrowband';
export const BROADBAND = 'broadband';
export const MULTIMEDIA = 'multimedia';
export const TELEPHONY = 'telephony';

// Default STT telephony models for specific language
export const DEFAULT_ARABIC_SPEECH_TO_TEXT_MODEL = 'ar-MS_Telephony';
export const DEFAULT_CZECH_SPEECH_TO_TEXT_MODEL = 'cs-CZ_Telephony';
export const DEFAULT_DUTCH_SPEECH_TO_TEXT_MODEL = 'nl-NL_Telephony';
export const DEFAULT_ENGLISH_SPEECH_TO_TEXT_MODEL = 'en-US_Telephony';
export const DEFAULT_FRENCH_SPEECH_TO_TEXT_MODEL = 'fr-FR_Telephony';
export const DEFAULT_GERMAN_SPEECH_TO_TEXT_MODEL = 'de-DE_Telephony';
export const DEFAULT_ITALIAN_SPEECH_TO_TEXT_MODEL = 'it-IT_Telephony';
export const DEFAULT_JAPANESE_SPEECH_TO_TEXT_MODEL = 'ja-JP_NarrowbandModel';
export const DEFAULT_KOREAN_SPEECH_TO_TEXT_MODEL = 'ko-KR_Telephony';
export const DEFAULT_SPANISH_SPEECH_TO_TEXT_MODEL = 'es-LA_Telephony';

// Default Large speech models (lsm) STT telephony
// As of writing this only english lsm is avilable
export const DEFAULT_ENGLISH_SPEECH_TO_TEXT_MODEL_LSM = 'en-US';

// Default TTS voice models for specific language
export const DEFAULT_CZECH_TEXT_TO_SPEECH_MODEL = 'cs-CZ_AlenaVoice';
export const DEFAULT_CHINESE_TEXT_TO_SPEECH_MODEL = 'zh-CN_WangWeiVoice';
export const DEFAULT_ARABIC_TEXT_TO_SPEECH_MODEL = 'ar-MS_OmarVoice';
export const DEFAULT_DUTCH_BELGIAN_TEXT_TO_SPEECH_MODEL = 'nl-BE_AdeleVoice';
export const DEFAULT_DUTCH_NETHERL_TEXT_TO_SPEECH_MODEL = 'nl-NL_EmmaVoice';
export const DEFAULT_ENGLISH_US_TEXT_TO_SPEECH_MODEL = 'en-US_EmmaExpressive';
export const DEFAULT_ENGLISH_US_TEXT_TO_SPEECH_MODEL_CPD =
  'en-US_LisaExpressive';
export const DEFAULT_ENGLISH_UK_TEXT_TO_SPEECH_MODEL = 'en-GB_CharlotteV3Voice';
export const DEFAULT_ENGLISH_AU_TEXT_TO_SPEECH_MODEL = 'en-AU_CraigVoice';
export const DEFAULT_FRENCH_CAN_TEXT_TO_SPEECH_MODEL = 'fr-CA_LouiseV3Voice';
export const DEFAULT_FRENCH_FRANCE_TEXT_TO_SPEECH_MODEL = 'fr-FR_ReneeV3Voice';
export const DEFAULT_GERMAN_TEXT_TO_SPEECH_MODEL = 'de-DE_DieterV3Voice';
export const DEFAULT_ITALIAN_TEXT_TO_SPEECH_MODEL = 'it-IT_FrancescaV3Voice';
export const DEFAULT_JAPANESE_TEXT_TO_SPEECH_MODEL = 'ja-JP_EmiV3Voice';
export const DEFAULT_KOREAN_TEXT_TO_SPEECH_MODEL = 'ko-KR_SiWooVoice';
export const DEFAULT_PORTUGUESE_TEXT_TO_SPEECH_MODEL = 'pt-BR_IsabelaV3Voice';
export const DEFAULT_SPANISH_CASTILIAN_TEXT_TO_SPEECH_MODEL =
  'es-ES_EnriqueV3Voice';
export const DEFAULT_SPANISH_NA_TEXT_TO_SPEECH_MODEL = 'es-US_SofiaV3Voice';
export const DEFAULT_SWEDISH_TEXT_TO_SPEECH_MODEL = 'sv-SE_IngridVoice';

export const TTS_PROVIDER_WATSON_ID = 'watson_tts';
export const TTS_PROVIDER_ELEVENLABS_ID = 'eleven_labs';

export const TTS_PROVIDER_ELEVENLABS_DEFAULT_MODEL_ID = 'eleven_flash_v2_5';
export const TTS_PROVIDER_ELEVENLABS_DEFAULT_VOICE_NAME = 'Jessica';

// Default TTS voice text for specified language
export const DEFAULT_TEXT_TO_SPEECH_VISUALIZER_TEXT = {
  ar: 'مرحبا، كيف يمكنني مساعدتك؟',
  cs: 'Dobrý den, jak vám mohu pomoci?',
  de: 'Hallo. Wie kann ich Ihnen helfen?',
  en: 'Hello. How can I help you?',
  es: 'Hola. ¿Cómo puedo ayudarle?',
  fr: 'Bonjour, comment puis-je vous aider ?',
  it: 'Salve. Come posso aiutarti?',
  ja: 'いらっしゃいませ。ご用件を入力してください。',
  ko: '안녕하세요? 무엇을 도와드릴까요?',
  nl: 'Hallo. Wat kan ik voor u doen?',
  pt: 'Olá. Como posso te ajudar?',
  'zh-CN': '您好。我能帮您什么？',
  'zh-TW': '您好，有什麼能為您服務的地方？'
};
export const DEFAULT_AR_TEXT = 'مرحبا، كيف يمكنني مساعدتك؟';
export const DEFAULT_CS_TEXT = 'Dobrý den, jak vám mohu pomoci?';
export const DEFAULT_DE_TEXT = 'Hallo. Wie kann ich Ihnen helfen?';
export const DEFAULT_EN_TEXT = 'Hello. How can I help you?';
export const DEFAULT_ES_TEXT = 'Hola. ¿Cómo puedo ayudarle?';
export const DEFAULT_FR_TEXT = 'Bonjour, comment puis-je vous aider ?';
export const DEFAULT_IT_TEXT = 'Salve. Come posso aiutarti?';
export const DEFAULT_JA_TEXT = 'いらっしゃいませ。ご用件を入力してください。';
export const DEFAULT_KO_TEXT = '안녕하세요? 무엇을 도와드릴까요?';
export const DEFAULT_NL_TEXT = 'Hallo. Wat kan ik voor u doen?';
export const DEFAULT_PT_TEXT = 'Olá. Como posso te ajudar?';
export const DEFAULT_ZH_CN_TEXT = '您好。我能帮您什么？';
export const DEFAULT_ZH_TW_TEXT = '您好，有什麼能為您服務的地方？';

export const MODAL_STATUS = {
  OPEN: 'open',
  CLOSED: 'closed',
  CANCELLED: 'cancelled'
};

export const AUDIO_VISUALIZATION_DIMENSIONS = {
  DATA_POINT_WIDTH: 2,
  DATA_POINT_HEIGHT: 75,
  DATA_POINT_MARGIN: 7,
  DATA_POINT_X_OFFSET: 16,
  DATA_POINT_Y_OFFSET: 50
};

export const MAX_TEXT_LENGTH = 100;
export const DEFAULT_NICE_CXONE_CONTACT_ID = 'X-ContactID';
export const DEFAULT_VOICE_SPEED = 0;
export const VOICE_SPEED = {
  MIN: -50,
  MAX: 100,
  STEP: 1
};
export const ELEVEN_LABS_DEFAULT_VOICE_SPEED = 0;
export const ELEVEN_LABS_DEFAULT_VOICE_STABILITY = 0;
export const ELEVEN_LABS_DEFAULT_VOICE_STYLE = 0;
export const ELEVEN_LABS_DEFAULT_VOICE_SIMILARITY = 0;
export const ELEVEN_LABS_DEFAULT_VOICE_SPEAKER_BOOST = true;
export const ELEVEN_LABS_VOICE_SPEED = {
  MIN: 0.7,
  MAX: 1.2,
  STEP: 0.1
};
export const ELEVEN_LABS_VOICE_STABILITY = {
  MIN: 0,
  MAX: 100,
  STEP: 1
};
export const ELEVEN_LABS_VOICE_STYLE = {
  MIN: 0.0,
  MAX: 1.0,
  STEP: 0.1
};
export const ELEVEN_LABS_VOICE_SIMILARITY = {
  MIN: 0,
  MAX: 100,
  STEP: 1
};
export const DEFAULT_VOICE_PITCH = 0;
export const VOICE_PITCH = {
  MIN: -50,
  MAX: 100,
  STEP: 1
};
